import { useTranslation } from 'core/i18n/i18n';
import { ErrorMapping } from 'core/types/error-mapping';
import { showErrorNotification, showSuccessNotification } from 'core/utils/notification-service';
import { updateQuery } from 'core/query-client/query-client';
import { QUERY_KEYS } from 'core/query-client/query-keys.constants';
import { useState } from 'react';
import {
    buildCreatePayload,
    buildUpdatePayload,
} from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-custom-alarm-form/hooks/use-custom-alarms-form/custom-alarms-form.mapper';
import { getCustomNotificationNameValidationCode } from 'modules/settings/components/custom-notifications/custom-notification-name-error.business';
import { CustomAlarmFormState } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-custom-alarm-form/settings-devices-custom-alarm-form.model';
import {
    createCustomAlarmDefinition,
    updateCustomAlarmDefinition,
} from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-custom-alarm-form/service/custom-alarm-form.service';
import { CustomAlarmDefinitionTrackableEvent } from 'modules/settings/pods/settings-devices/settings-devices-configuration/domain/custom-alarm-definition.vm';

interface UseCustomAlarmsSaverProps {
    deviceTypeId: string;
    onSuccess: VoidFunction;
}

export const useCustomAlarmsSaver = ({ deviceTypeId, onSuccess }: UseCustomAlarmsSaverProps) => {
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [nameCustomValidationCode, setNameCustomValidationCode] = useState<ErrorMapping['type'] | undefined>();

    const resetNameCustomValidationCode = () => {
        setNameCustomValidationCode(undefined);
    };

    const saveCustomAlarm = (form: CustomAlarmFormState, selectedEvents: CustomAlarmDefinitionTrackableEvent[]) => {
        setIsSubmitting(true);

        return createCustomAlarmDefinition(buildCreatePayload(form, selectedEvents, deviceTypeId))
            .then(() => {
                showSuccessNotification(t('settings.devices.custom-alarms.create.modal.notifications.success'));
                onSuccess();
            })
            .catch((error: Error) => {
                setNameCustomValidationCode(getCustomNotificationNameValidationCode(error));
                showErrorNotification(t('settings.devices.custom-alarms.create.modal.notifications.error'));
            })
            .finally(() => setIsSubmitting(false));
    };

    const editCustomAlarm = (
        form: CustomAlarmFormState,
        selectedEvents: CustomAlarmDefinitionTrackableEvent[],
        definitionId: string
    ) => {
        setIsSubmitting(true);
        const payload = buildUpdatePayload(form, selectedEvents, deviceTypeId);

        return updateCustomAlarmDefinition(payload, definitionId)
            .then(() => {
                updateQuery(QUERY_KEYS.CUSTOM_ALARM_DEFINITION_ID(definitionId));
                showSuccessNotification(t('settings.devices.custom-alarms.edit.modal.notifications.success'));
                onSuccess();
            })
            .catch((error: Error) => {
                setNameCustomValidationCode(getCustomNotificationNameValidationCode(error));
                showErrorNotification(t('settings.devices.custom-alarms.edit.modal.notifications.error'));
            })
            .finally(() => setIsSubmitting(false));
    };

    return {
        isSubmitting,
        nameCustomValidationCode,
        resetNameCustomValidationCode,
        saveCustomAlarm,
        editCustomAlarm,
    };
};
